import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Checkbox } from 'primereact/checkbox';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Message } from 'primereact/message';
import { equipmentCategoriesAPI } from '../services/api';
import { EquipmentCategory } from '../types';

const ITEquipmentCategories: React.FC = () => {
  const toast = useRef<Toast>(null);

  const [items, setItems] = useState<EquipmentCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [globalFilter, setGlobalFilter] = useState('');

  const [editVisible, setEditVisible] = useState(false);
  const [editing, setEditing] = useState<EquipmentCategory | null>(null);
  const [form, setForm] = useState({
    name: '',
    icon: 'pi pi-laptop',
    color: '#3B82F6',
    isActive: true,
    description: ''
  });

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await equipmentCategoriesAPI.getAll();
      setItems(data);
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error(e);
      setError(e?.response?.data?.error || 'Greška pri učitavanju kategorija opreme');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const resetForm = () => {
    setEditing(null);
    setForm({
      name: '',
      icon: 'pi pi-laptop',
      color: '#3B82F6',
      isActive: true,
      description: ''
    });
  };

  const openNew = () => {
    resetForm();
    setEditVisible(true);
  };

  const openEdit = (row: EquipmentCategory) => {
    setEditing(row);
    setForm({
      name: row.name || '',
      icon: row.icon || 'pi pi-laptop',
      color: row.color || '#3B82F6',
      isActive: row.isActive ?? true,
      description: row.description || ''
    });
    setEditVisible(true);
  };

  const confirmDelete = (row: EquipmentCategory) => {
    confirmDialog({
      header: 'Potvrda brisanja',
      message: `Obrisati kategoriju "${row.name}"?`,
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          await equipmentCategoriesAPI.delete(row._id);
          toast.current?.show({ severity: 'success', summary: 'Uspešno', detail: 'Kategorija obrisana', life: 2500 });
          await load();
        } catch (e: any) {
          toast.current?.show({
            severity: 'error',
            summary: 'Greška',
            detail: e?.response?.data?.error || 'Greška pri brisanju kategorije',
            life: 5000
          });
        }
      }
    });
  };

  const canSubmit = useMemo(() => !!form.name.trim(), [form.name]);

  const handleSubmit = async () => {
    if (!canSubmit) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Validacija',
        detail: 'Naziv kategorije je obavezan',
        life: 3000
      });
      return;
    }

    setLoading(true);
    try {
      if (editing) {
        await equipmentCategoriesAPI.update(editing._id, {
          name: form.name,
          icon: form.icon,
          color: form.color,
          isActive: form.isActive,
          description: form.description
        });
        toast.current?.show({ severity: 'success', summary: 'Uspešno', detail: 'Kategorija ažurirana', life: 2500 });
      } else {
        await equipmentCategoriesAPI.create({
          name: form.name,
          icon: form.icon,
          color: form.color,
          isActive: form.isActive,
          description: form.description
        });
        toast.current?.show({ severity: 'success', summary: 'Uspešno', detail: 'Kategorija kreirana', life: 2500 });
      }
      setEditVisible(false);
      await load();
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.current?.show({
        severity: 'error',
        summary: 'Greška',
        detail: e?.response?.data?.error || 'Greška pri čuvanju kategorije',
        life: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const categoryBody = (row: EquipmentCategory) => (
    <div className="flex align-items-center gap-2">
      <i className={row.icon} style={{ color: row.color }} />
      <span>{row.name}</span>
    </div>
  );

  const statusBody = (row: EquipmentCategory) => (
    <Tag value={row.isActive ? 'Aktivna' : 'Neaktivna'} severity={row.isActive ? 'success' : 'danger'} />
  );

  const colorBody = (row: EquipmentCategory) => (
    <div className="flex align-items-center gap-2">
      <span
        style={{
          width: '1rem',
          height: '1rem',
          borderRadius: '999px',
          backgroundColor: row.color || '#CCCCCC',
          border: '1px solid #e5e7eb'
        }}
      />
      <span>{row.color}</span>
    </div>
  );

  const actionsBody = (row: EquipmentCategory) => (
    <div className="flex gap-2">
      <Button
        icon="pi pi-pencil"
        className="p-button-sm p-button-rounded p-button-text"
        onClick={() => openEdit(row)}
        tooltip="Uredi"
      />
      <Button
        icon="pi pi-trash"
        className="p-button-sm p-button-rounded p-button-text p-button-danger"
        onClick={() => confirmDelete(row)}
        tooltip="Obriši"
      />
    </div>
  );

  return (
    <div className="p-4">
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h2 className="m-0">Kategorije opreme</h2>
          <div className="text-600 mt-1">Upravljanje kategorijama opreme u sistemu</div>
        </div>
        <Button label="Dodaj kategoriju" icon="pi pi-plus" onClick={openNew} />
      </div>

      {error && <Message severity="error" text={error} className="mb-3" />}

      <Card>
        <div className="flex justify-content-end mb-3">
          <span className="p-input-icon-left" style={{ width: 'min(380px, 100%)' }}>
            <i className="pi pi-search" />
            <InputText
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              placeholder="Pretraga..."
              style={{ width: '100%' }}
            />
          </span>
        </div>

        <DataTable
          value={items}
          loading={loading}
          paginator
          rows={10}
          emptyMessage="Nema kategorija"
          className="p-datatable-sm"
          globalFilter={globalFilter}
          globalFilterFields={['name', 'description', 'color']}
          responsiveLayout="stack"
          breakpoint="768px"
        >
          <Column field="name" header="Kategorija" body={categoryBody} sortable />
          <Column field="description" header="Opis" sortable />
          <Column field="isActive" header="Status" body={statusBody} sortable style={{ width: '140px' }} />
          <Column field="color" header="Boja" body={colorBody} sortable style={{ width: '160px' }} />
          <Column header="Akcije" body={actionsBody} style={{ width: '140px' }} />
        </DataTable>
      </Card>

      <Dialog
        header={editing ? 'Izmeni kategoriju' : 'Nova kategorija'}
        visible={editVisible}
        onHide={() => setEditVisible(false)}
        style={{ width: '700px' }}
        breakpoints={{ '960px': '95vw', '640px': '98vw' }}
        modal
        draggable={false}
        footer={
          <div className="flex justify-content-end gap-2">
            <Button
              label="Otkaži"
              className="p-button-secondary p-button-outlined"
              onClick={() => setEditVisible(false)}
            />
            <Button
              label={editing ? 'Sačuvaj' : 'Kreiraj'}
              icon="pi pi-check"
              onClick={handleSubmit}
              loading={loading}
              disabled={!canSubmit}
            />
          </div>
        }
      >
        <div className="p-fluid formgrid grid">
          <div className="field col-12 md:col-6">
            <label>Naziv kategorije *</label>
            <InputText value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
          </div>
          <div className="field col-12 md:col-6">
            <label>Ikona</label>
            <InputText value={form.icon} onChange={(e) => setForm((p) => ({ ...p, icon: e.target.value }))} />
            <small>Primeri: pi pi-laptop, pi pi-desktop, pi pi-phone, pi pi-print</small>
          </div>
          <div className="field col-12 md:col-6">
            <label>Boja</label>
            <InputText value={form.color} onChange={(e) => setForm((p) => ({ ...p, color: e.target.value }))} />
          </div>
          <div className="field col-12 md:col-6 flex align-items-end">
            <div className="flex align-items-center gap-2">
              <Checkbox
                inputId="category-active"
                checked={form.isActive}
                onChange={(e) => setForm((p) => ({ ...p, isActive: e.checked ?? false }))}
              />
              <label htmlFor="category-active" className="mb-0">
                Aktivna
              </label>
            </div>
          </div>
          <div className="field col-12">
            <label>Opis</label>
            <InputTextarea
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              rows={4}
              autoResize
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default ITEquipmentCategories;






